import { HelpCircle } from "lucide-react";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "../../components/ui/accordion";

/**
 * Hilfe & Über Platzwart -- kompakte In-App-Referenz für das Lager-Team.
 * Quelle: docs/PLATZWART-EINSTIEG.md sowie docs/BENUTZERHANDBUCH.md
 * und docs/UEBERGABE-DATACENTER-TEAM.md (§4 Meldeweg). Kein 1:1-Spiegel des
 * Handbuchs -- nur das, was am Gerät gebraucht wird. Bei Handbuch-Updates
 * die Abschnitte "Einschränkungen" und "Problem melden" mitziehen.
 */

const FLOWS = [
    ["Material entnehmen", "Fach scannen → Artikel wählen → Menge → Grund → Buchen."],
    ["Umlagern", "Quell-Fach scannen → Artikel + Menge → Ziel-Fach scannen → Buchen."],
    ["Wareneingang", "Aktionen → Wareneingang → Positionen erfassen (bei Geräten Seriennummern) → Buchen. Nur online."],
    ["Gerät oder Schlüssel ausleihen", "Geräte- oder Schlüssel-QR scannen → Ausleihe → Person/Kontakt angeben → Buchen. Wer gerade was hat: Mehr → „Ausleihen — wer hat was?“."],
    ["Gerät oder Schlüssel zurückgeben", "Geräte- oder Schlüssel-QR scannen → Rückgabe → bei Schäden Zustand und Foto ergänzen → Buchen."],
    ["Gegenstand aufnehmen", "Aktionen → Gegenstand aufnehmen → Foto + Bezeichnung → zur Prüfung senden. Nur online; ein Admin übernimmt ihn bei Bedarf."],
    ["Korrektur", "Nur bei echter Differenz: Fach scannen → Korrektur → gezählten Gesamtbestand + Grund + Kommentar."],
    ["Entsorgung", "Aktionen → Entsorgung → Grund + Foto → Buchen. Nur online; das Foto ist Pflicht."],
];

export default function Hilfe() {
    return (
        <section className="flex flex-col gap-4" data-testid="hilfe-page">
            <div>
                <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Floor Mode</div>
                <h1 className="text-3xl font-bold tracking-tight text-foreground">Hilfe</h1>
            </div>

            <div className="flex items-start gap-3 rounded-xl border border-border bg-card/60 p-4">
                <HelpCircle className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                <p className="text-sm text-muted-foreground">
                    Der schnellste Einstieg ist fast immer: <span className="text-foreground">QR-Code scannen</span> —
                    die App bietet dann die passenden Aktionen an. Ausführliche Anleitungen stehen im
                    Benutzerhandbuch (beim Admin / bei der Einweisung erhalten).
                </p>
            </div>

            <Accordion type="single" collapsible className="w-full" data-testid="hilfe-accordion">
                <AccordionItem value="start">
                    <AccordionTrigger>Lager einrichten – die ersten vier Schritte</AccordionTrigger>
                    <AccordionContent className="flex flex-col gap-3 text-sm text-muted-foreground">
                        <p>
                            Bevor das Team im Alltag bucht, wird das Lager erst an einem Pilotbereich arbeitsfähig gemacht.
                        </p>
                        <ol className="list-decimal space-y-2 pl-5">
                            <li><span className="font-medium text-foreground">Lagerbild festlegen:</span> Fachliste mit Raum, Regal, Fach und Fach-Code erstellen; ein Pilotregal auswählen.</li>
                            <li><span className="font-medium text-foreground">Artikelstamm aufräumen:</span> Importierte Artikel bestätigen, Korrekturen sammeln, echte Neuanlagen als Artikel anlegen und unklare Teile fotografieren.</li>
                            <li><span className="font-medium text-foreground">Bestand einsortieren:</span> Aus Import- oder Staging-Fächern per Umlagerung in die endgültigen Fächer buchen; Abweichungen zählen und korrigieren.</li>
                            <li><span className="font-medium text-foreground">Go-live absichern:</span> QR-Etiketten kleben, Erstinventur zählen und Entnahme, Umlagerung, Ausleihe und Rückgabe einmal gemeinsam testen.</li>
                        </ol>
                        <p>
                            Admins können Standorte, Räume, Regale und Fächer selbst anlegen, über den Stift bearbeiten und alphabetisch sortiert verwalten. Fach-QR-Etiketten werden dort als PNG gespeichert und anschließend extern gedruckt.
                        </p>
                    </AccordionContent>
                </AccordionItem>

                <AccordionItem value="flows">
                    <AccordionTrigger>Abläufe im Alltag (Kurzreferenz)</AccordionTrigger>
                    <AccordionContent>
                        <ul className="flex flex-col gap-2 text-sm text-muted-foreground">
                            {FLOWS.map(([title, text]) => (
                                <li key={title}>
                                    <span className="font-medium text-foreground">{title}: </span>
                                    {text}
                                </li>
                            ))}
                        </ul>
                    </AccordionContent>
                </AccordionItem>

                <AccordionItem value="scan">
                    <AccordionTrigger>Scannen</AccordionTrigger>
                    <AccordionContent className="text-sm text-muted-foreground">
                        <p>
                            Jedes Fach und jedes Gerät hat ein QR-Etikett. Scan öffnet die Kamera;
                            nach dem Scan zeigt die App kontextbezogen die möglichen Aktionen
                            (z. B. Fach → Entnahme/Umlagerung, Gerät → Ausleihe/Rückgabe).
                            Alternativ lässt sich der Code unten im Scan-Bildschirm von Hand eingeben.
                        </p>
                    </AccordionContent>
                </AccordionItem>

                <AccordionItem value="offline">
                    <AccordionTrigger>Offline arbeiten</AccordionTrigger>
                    <AccordionContent className="flex flex-col gap-2 text-sm text-muted-foreground">
                        <p>
                            Kein Offline-Start: App und Ablauf müssen mit Netz geöffnet sein.
                            Bricht die Verbindung danach ab, können bestimmte Buchungen in der
                            Warteschlange unter „Mehr" warten und werden später automatisch
                            gesendet. Doppelt gebucht wird dabei nicht.
                        </p>
                        <p>
                            Braucht Netz: Wareneingang, Inventur, Pickliste, Gegenstand aufnehmen,
                            Entsorgungs-Pflichtfoto und alle anderen Nachweis-Fotos.
                        </p>
                        <p>
                            Warteschlangen gehören zum angemeldeten Benutzer: Nach dem Abmelden
                            bleiben wartende Buchungen auf dem Gerät gespeichert und werden erst
                            wieder gesendet, wenn sich derselbe Benutzer anmeldet. Andere Benutzer
                            sehen sie nicht.
                        </p>
                        <p>
                            Zeigt ein Eintrag dauerhaft „Konflikt": zuerst im Verlauf prüfen, ob die
                            Buchung schon da ist. Falls nein: Eintrag verwerfen und regulär neu buchen.
                        </p>
                    </AccordionContent>
                </AccordionItem>

                <AccordionItem value="limits">
                    <AccordionTrigger>Bekannte Einschränkungen</AccordionTrigger>
                    <AccordionContent className="flex flex-col gap-2 text-sm text-muted-foreground">
                        <p className="font-medium text-foreground">
                            Pickliste: „Abschließen" bucht die gepickten Mengen als Entnahme.
                            Vorher die Mengen je Position prüfen — nicht Gepicktes auf 0 stellen.
                        </p>
                        <p>
                            Beim Lagerstart: Bestehende Importartikel nicht vorschnell doppelt anlegen. Erst zuordnen oder Korrekturen gesammelt an AppWerkstatt geben.
                        </p>
                        <p>
                            In seltenen Fehlerfällen kann ein Foto unverknüpft bleiben oder doppelt
                            hochgeladen werden — bitte melden statt neu fotografieren oder neu buchen.
                        </p>
                    </AccordionContent>
                </AccordionItem>

                <AccordionItem value="konto">
                    <AccordionTrigger>Anmeldung und Passwort</AccordionTrigger>
                    <AccordionContent className="flex flex-col gap-2 text-sm text-muted-foreground">
                        <p>
                            Passwort ändern: <span className="font-medium text-foreground">Mehr → Einstellungen → „Passwort ändern"</span> —
                            aktuelles Passwort eingeben, neues mit mindestens 8 Zeichen wählen.
                            Das Startpasswort vom Admin am besten gleich nach der ersten Anmeldung ändern.
                        </p>
                        <p>
                            Passwort vergessen: Admin ansprechen — er setzt unter Admin → Benutzer ein
                            neues Passwort. Dabei werden alle Sitzungen abgemeldet.
                        </p>
                        <p>
                            Anzeigename ändern: <span className="font-medium text-foreground">Mehr → Einstellungen → „Anzeigename ändern"</span> —
                            der Name erscheint im Gruß auf der Startseite und im Leitstand.
                        </p>
                        <p>
                            Nach 5 Fehlversuchen ist die Anmeldung 15 Minuten gesperrt — warten und
                            erneut versuchen.
                        </p>
                    </AccordionContent>
                </AccordionItem>

                <AccordionItem value="melden">
                    <AccordionTrigger>Problem melden</AccordionTrigger>
                    <AccordionContent className="flex flex-col gap-2 text-sm text-muted-foreground">
                        <p>
                            Erstkontakt: AppWerkstatt (Mathias Wiedmer) — per Mail, bei dringenden
                            Fällen telefonisch. Kontaktdaten gibt es beim Admin und in der Einweisung.
                        </p>
                        <p>
                            Bitte pro Thema eine kurze, separate Mail schreiben: <span className="font-medium text-foreground">Problem</span>, <span className="font-medium text-foreground">Wunsch</span>, <span className="font-medium text-foreground">Frage</span> oder <span className="font-medium text-foreground">Feature</span> im Betreff. So lässt sich jedes Thema sauber verfolgen.
                        </p>
                        <p>
                            Was bei einer Meldung hilft: Screenshot, Uhrzeit, was gescannt oder gebucht
                            wurde, und ob offline gearbeitet wurde.
                        </p>
                        <p>
                            Wichtig: Buchungen gehen nicht verloren — im Zweifel nicht mehrfach von
                            Hand nachbuchen, sondern erst melden.
                        </p>
                    </AccordionContent>
                </AccordionItem>

                <AccordionItem value="about">
                    <AccordionTrigger>Über Platzwart</AccordionTrigger>
                    <AccordionContent className="flex flex-col gap-2 text-sm text-muted-foreground">
                        <p>
                            Platzwart ist die Lager- und Inventarverwaltung des Teams. Jede
                            Bestandsänderung wird lückenlos und unveränderlich protokolliert.
                        </p>
                        <p>
                            Betrieb und Wartung: AppWerkstatt (Mathias Wiedmer). Gehostet in
                            Deutschland; nächtliche Backups und Überwachung sind eingerichtet.
                        </p>
                    </AccordionContent>
                </AccordionItem>
            </Accordion>
        </section>
    );
}
